import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type BioLinkKind = "link" | "video" | "blog_post" | "social";
export type BioVideoProvider = "youtube" | "instagram" | "tiktok";

export interface BioLinkBlogPost {
  id: string;
  title: string;
  slug: string;
  cover_image_url: string | null;
  published: boolean;
}

export interface BioLink {
  id: string;
  kind: BioLinkKind;
  title: string | null;
  description: string | null;
  url: string | null;
  icon: string | null;
  video_url: string | null;
  video_provider: BioVideoProvider | null;
  blog_post_id: string | null;
  is_active: boolean;
  is_primary: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  blog_posts?: BioLinkBlogPost | null;
}

export interface CreateBioLinkData {
  kind: BioLinkKind;
  title?: string;
  description?: string;
  url?: string;
  icon?: string;
  video_url?: string;
  video_provider?: BioVideoProvider;
  blog_post_id?: string;
  is_active?: boolean;
  is_primary?: boolean;
  display_order?: number;
}

export type UpdateBioLinkData = Partial<CreateBioLinkData>;

// Only one card is the accent "destaque": flagging one un-flags the rest.
async function clearOtherPrimary(exceptId?: string) {
  let q = supabase
    .from("bio_links" as never)
    .update({ is_primary: false } as never)
    .eq("is_primary", true);
  if (exceptId) q = q.neq("id", exceptId);
  const { error } = await q;
  if (error) throw error;
}

const BLOG_POST_JOIN = "blog_posts(id, title, slug, cover_image_url, published)";

// Lista pública: só itens ativos, na ordem de exibição
export function usePublicBioLinks() {
  return useQuery({
    queryKey: ["public-bio-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bio_links" as never)
        .select(`*, ${BLOG_POST_JOIN}`)
        .eq("is_active", true)
        .is("deleted_at", null)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as unknown as BioLink[];
    },
  });
}

// Lista completa (admin)
export function useBioLinks() {
  return useQuery({
    queryKey: ["bio-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bio_links" as never)
        .select(`*, ${BLOG_POST_JOIN}`)
        .is("deleted_at", null)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as unknown as BioLink[];
    },
  });
}

export function useCreateBioLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBioLinkData) => {
      if (data.is_primary) await clearOtherPrimary();
      const { data: link, error } = await supabase
        .from("bio_links" as never)
        .insert({
          kind: data.kind,
          title: data.title || null,
          description: data.description || null,
          url: data.url || null,
          icon: data.icon || null,
          video_url: data.video_url || null,
          video_provider: data.video_provider || null,
          blog_post_id: data.blog_post_id || null,
          is_active: data.is_active ?? true,
          is_primary: data.is_primary ?? false,
          display_order: data.display_order ?? 0,
        } as never)
        .select()
        .single();

      if (error) throw error;
      return link as unknown as BioLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bio-links"] });
      queryClient.invalidateQueries({ queryKey: ["public-bio-links"] });
      toast.success("Link criado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao criar link: " + error.message);
    },
  });
}

export function useUpdateBioLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateBioLinkData }) => {
      if (data.is_primary) await clearOtherPrimary(id);
      const { data: link, error } = await supabase
        .from("bio_links" as never)
        .update(data as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return link as unknown as BioLink;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bio-links"] });
      queryClient.invalidateQueries({ queryKey: ["public-bio-links"] });
      toast.success("Link atualizado com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao atualizar link: " + error.message);
    },
  });
}

export function useDeleteBioLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("bio_links" as never)
        .update({ deleted_at: new Date().toISOString() } as never)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bio-links"] });
      queryClient.invalidateQueries({ queryKey: ["public-bio-links"] });
      toast.success("Link removido com sucesso!");
    },
    onError: (error) => {
      toast.error("Erro ao remover link: " + error.message);
    },
  });
}

export function useToggleBioLinkActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, currentActive }: { id: string; currentActive: boolean }) => {
      const { data: link, error } = await supabase
        .from("bio_links" as never)
        .update({ is_active: !currentActive } as never)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return link as unknown as BioLink;
    },
    onSuccess: (link) => {
      queryClient.invalidateQueries({ queryKey: ["bio-links"] });
      queryClient.invalidateQueries({ queryKey: ["public-bio-links"] });
      toast.success(link.is_active ? "Link ativado!" : "Link desativado.");
    },
    onError: (error) => {
      toast.error("Erro ao alterar status: " + error.message);
    },
  });
}

// Reordena um conjunto de itens (uma "zona" da lista) após um drag-and-drop
export function useReorderBioLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: { id: string; display_order: number }[]) => {
      await Promise.all(
        items.map(({ id, display_order }) =>
          supabase
            .from("bio_links" as never)
            .update({ display_order } as never)
            .eq("id", id),
        ),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bio-links"] });
      queryClient.invalidateQueries({ queryKey: ["public-bio-links"] });
    },
    onError: (error) => {
      toast.error("Erro ao reordenar links: " + error.message);
    },
  });
}

// Contagem de cliques por link, últimos N dias (metadata.link_id em analytics_events)
export function useBioLinkClickStats(days = 30) {
  return useQuery({
    queryKey: ["bio-link-click-stats", days],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from("analytics_events" as never)
        .select("metadata")
        .eq("event_type", "link_click")
        .gte("created_at", startDate.toISOString());

      if (error) throw error;

      const counts: Record<string, number> = {};
      for (const row of (data || []) as { metadata: { link_id?: string } | null }[]) {
        const linkId = row.metadata?.link_id;
        if (!linkId) continue;
        counts[linkId] = (counts[linkId] || 0) + 1;
      }
      return counts;
    },
  });
}

// O seletor de post de blog usa o dono do domínio (useBlogPosts). Reexport
// mantido para o import atual de LinksManager.
export { usePublishedBlogPosts } from "@/hooks/useBlogPosts";
